import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", membership_number: "", licence_number: "", qualification: "", qualification_date: "", member_type: "new", previous_pass_date: "", notes: "", active: true };

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export const RiderFormDialog = ({ open, onOpenChange, rider, onSaved }) => {
  const [f, setF] = useState(rider ? { ...EMPTY, ...rider } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const canSetPrevious = !rider || !rider.assessment_count;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? EMPTY[k]]));
      const { data } = rider ? await api.patch(`/riders/${rider.id}`, payload) : await api.post("/riders", payload);
      toast.success(rider ? "Rider updated" : "Rider added");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="rider-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{rider ? "Edit rider" : "Add rider"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <Field label="First name"><Input required value={f.first_name} onChange={set("first_name")} data-testid="rider-first-name-input" /></Field>
          <Field label="Last name"><Input required value={f.last_name} onChange={set("last_name")} data-testid="rider-last-name-input" /></Field>
          <Field label="Email"><Input type="email" value={f.email} onChange={set("email")} data-testid="rider-email-input" /></Field>
          <Field label="Phone"><Input value={f.phone} onChange={set("phone")} data-testid="rider-phone-input" /></Field>
          <Field label="Membership no."><Input value={f.membership_number} onChange={set("membership_number")} data-testid="rider-membership-input" /></Field>
          <Field label="Driving licence no."><Input value={f.licence_number} onChange={set("licence_number")} data-testid="rider-licence-input" /></Field>
          <Field label="Advanced qualification">
            <Select value={f.qualification || undefined} onValueChange={set("qualification")}>
              <SelectTrigger data-testid="rider-qualification-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {["IAM Advanced", "IAM Masters", "RoSPA Bronze", "RoSPA Silver", "RoSPA Gold", "Police", "Other"].map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Qualification date"><Input type="date" value={f.qualification_date} onChange={set("qualification_date")} data-testid="rider-qualification-date-input" /></Field>
          <Field label="Member type">
            <Select value={f.member_type} onValueChange={set("member_type")}>
              <SelectTrigger data-testid="rider-member-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New member</SelectItem>
                <SelectItem value="existing">Existing member</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {canSetPrevious && (
            <Field label="Last check ride passed (before this system)">
              <Input type="date" value={f.previous_pass_date || ""} onChange={set("previous_pass_date")} data-testid="rider-previous-pass-input" />
            </Field>
          )}
          <Field label="Notes" className="sm:col-span-2"><Textarea rows={3} value={f.notes} onChange={set("notes")} data-testid="rider-notes-input" /></Field>
          {rider && (
            <label className="flex items-center gap-3 text-sm sm:col-span-2">
              <Switch checked={f.active} onCheckedChange={set("active")} data-testid="rider-active-switch" /> Active rider
            </label>
          )}
          <div className="flex justify-end gap-3 sm:col-span-2">
            <button type="button" onClick={() => onOpenChange(false)} className="rounded-full px-5 py-2.5 text-sm font-semibold text-muted-foreground hover:text-foreground">Cancel</button>
            <button type="submit" disabled={busy} data-testid="rider-save-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save rider
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
